import React, { useState } from 'react';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { WithdrawalRequest } from '../../../types';
import { useApproveWithdrawal } from '../../../hooks/useApi';
import { getWithdrawalListLabel } from '../../../utils/withdrawalRequester';
import { formatWithdrawalDisplayAmount } from '../../../utils/portalUsdWithdrawalDisplay';
import { LoadingSpinner } from '../../ui/Loading';

interface ApproveWithdrawalModalProps {
  withdrawal: WithdrawalRequest | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const parseErrorMessage = (error: any): string => {
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return 'An error occurred while approving withdrawal';
};

const ApproveWithdrawalModal: React.FC<ApproveWithdrawalModalProps> = ({
  withdrawal,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [adminNotes, setAdminNotes] = useState('');
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const approveWithdrawalMutation = useApproveWithdrawal();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!withdrawal) return;

    try {
      setApprovalError(null);
      await approveWithdrawalMutation.mutateAsync({
        transactionId: withdrawal.transactionId,
        data: {
          adminNotes: adminNotes.trim() || undefined,
        },
      });
      toast.success('Withdrawal approved successfully');
      onSuccess?.();
      onClose();
      setAdminNotes('');
    } catch (error: any) {
      console.error('Approve withdrawal error:', error);
      const errorMessage = parseErrorMessage(error);
      setApprovalError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    setAdminNotes('');
    setApprovalError(null);
    onClose();
  };

  if (!isOpen || !withdrawal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 !mt-0">
      <Card className="w-full max-w-md">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold">Approve Withdrawal</CardTitle>
          <Button variant="ghost" size="sm" onClick={handleClose}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {withdrawal.provider === 'linkmepay' && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm"
                role="alert"
              >
                <p className="font-medium">LinkMe Pay</p>
                <p className="mt-1">
                  You need to go to LinkMe Pay&apos;s payout management page to approve before the funds will be sent to the user&apos;s wallet.
                </p>
              </div>
            )}
            {withdrawal.provider === 'zeroxprocessing' && (
              <div
                className="rounded-lg border border-violet-200 bg-violet-50 p-4 text-violet-900 text-sm"
                role="alert"
              >
                <p className="font-medium">0xProcessing</p>
                <p className="mt-1">
                  Approving will process the withdrawal via 0xProcessing. Funds will be sent to the user&apos;s crypto address.
                </p>
              </div>
            )}
            {approvalError && (
              <div
                className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-900 text-sm"
                role="alert"
              >
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">Approval failed</p>
                    <p className="mt-1 break-words">{approvalError}</p>
                    <p className="mt-1 text-xs text-red-700">
                      Please check provider liquidity/balance and retry, or use manual process.
                    </p>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                Are you sure you want to approve this withdrawal request?
              </p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="text-sm">
                  <div className="font-medium">{withdrawal.transactionId}</div>
                  <div className="text-muted-foreground">
                    {getWithdrawalListLabel(withdrawal)}
                  </div>
                  <div className="font-medium text-red-600">
                    {formatWithdrawalDisplayAmount(withdrawal, { signed: true })}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="adminNotes" className="block text-sm font-medium mb-2">
                Admin Notes (Optional)
              </label>
              <Input
                id="adminNotes"
                type="text"
                placeholder="Enter admin notes..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                maxLength={1000}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {adminNotes.length}/1000 characters
              </p>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={approveWithdrawalMutation.isLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {approveWithdrawalMutation.isLoading ? (
                  <div className="flex items-center">
                    <LoadingSpinner />
                    <span className="ml-2">Approving...</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve Withdrawal
                  </div>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApproveWithdrawalModal;
